import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { OrderDA } from '../../services/admin/order.service';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';
import { StorageCenterDA } from '../../services/admin/storage.center.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Order } from '../../models/order.model';
import { Company } from '../../models/company.model';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { countryName } from '../../libraries/country.library';

import AppLoading from '../Loading';

export interface AppModalDeliveryNoteCreateProps {
  companyForUser: Company | null,
  order: Order | null,
  deliveryStatus: any,
  onCreated: () => void
};

interface DeliveryLine {
  requirement_id: string
  content_id: string
  detail: string
  reference: string | null
  is_service: boolean
  quantity_ordered: number
  quantity_delivered: number
  quantity_remaining: number
  quantity_in_stock: number
  sufficient: boolean
  quantityToDeliver: number
}

let errorResponse: Error;

const AppModalDeliveryNoteCreate: React.FunctionComponent<AppModalDeliveryNoteCreateProps> = ({companyForUser, order, deliveryStatus, onCreated}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const today = new Date().toISOString().split('T')[0]
  const [deliveryDate, setDeliveryDate] = useState(today)
  const [deliverAll, setDeliverAll] = useState(false)
  const [lines, setLines] = useState<DeliveryLine[]>([])

  const [addresses, setAddresses] = useState<any[]>([])
  const [selectedAddressId, setSelectedAddressId] = useState<string>('')
  const [storageCenters, setStorageCenters] = useState<any[]>([])
  const [selectedStorageCenterId, setSelectedStorageCenterId] = useState<string>('')
  const [autoStockOut, setAutoStockOut] = useState(true)
  const [stockAvailability, setStockAvailability] = useState<any[]>([])
  const [stockWarnings, setStockWarnings] = useState<string[]>([])

  useEffect(() => {
    if (order && deliveryStatus) {
      buildLines()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, deliveryStatus])

  useEffect(() => {
    if (companyForUser && order?.customer?.data?.id) {
      loadAddresses()
      loadStorageCenters()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, order])

  useEffect(() => {
    if (companyForUser && order && selectedStorageCenterId) {
      loadStockAvailability()
    } else {
      setStockAvailability([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStorageCenterId])

  const loadAddresses = async () => {
    if (!companyForUser || !order?.customer?.data?.id) return
    await ApiDigitalAdminContact.getContactAddresses(companyForUser.id, order.customer.data.id).then((response) => {
      if (response.status === 200) {
        const addrs = response.data ?? []
        setAddresses(addrs)
        const defaultAddr = addrs.find((a: any) => a.is_default_delivery)
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id)
        } else if (addrs.length > 0) {
          setSelectedAddressId(addrs[0].id)
        }
      }
    }).catch(() => {})
  }

  const loadStorageCenters = async () => {
    if (!companyForUser) return
    await StorageCenterDA.getStorageCenters(companyForUser.id, '', '', '').then((response) => {
      if (response.status === 200) {
        const centers = response.data?.storage_centers ?? response.data?.items ?? response.data ?? []
        setStorageCenters(Array.isArray(centers) ? centers : [])
      }
    }).catch(() => {})
  }

  const loadStockAvailability = async () => {
    if (!companyForUser || !order || !selectedStorageCenterId) return

    const variantIds: string[] = (order.requirements ?? [])
      .filter((req: any) => req.type === 'product')
      .flatMap((req: any) => (req.contents ?? [])
        .filter((c: any) => (c.reference ?? '').length > 0)
        .map((c: any) => c.reference)
      )

    if (variantIds.length === 0) return

    await OrderDA.getStockAvailability(companyForUser.id, variantIds, selectedStorageCenterId).then((response) => {
      if (response.status === 200) {
        const rawData = response.data ?? {}
        const availabilityArray: any[] = []

        if (typeof rawData === 'object' && !Array.isArray(rawData)) {
          for (const [variantId, centers] of Object.entries(rawData)) {
            if (Array.isArray(centers)) {
              for (const center of centers as any[]) {
                availabilityArray.push({
                  variant_id: variantId,
                  id_storage_center: center.id_storage_center,
                  quantity_in_stock: center.quantity ?? 0
                })
              }
            }
          }
        } else if (Array.isArray(rawData)) {
          availabilityArray.push(...rawData)
        }

        setStockAvailability(availabilityArray)
      }
    }).catch(() => {})
  }

  const buildLines = () => {
    if (!order) return

    const statusLines: any[] = deliveryStatus?.lines ?? []
    const result: DeliveryLine[] = []

    ;(order.requirements ?? []).forEach((req: any, reqIndex: number) => {
      ;(req.contents ?? []).forEach((content: any, contentIndex: number) => {
        const reqId = req.id ?? `req_${reqIndex}`
        const contentId = content.id ?? `content_${reqIndex}_${contentIndex}`

        const statusLine = statusLines.find(
          (sl: any) => sl.requirement_id === reqId && sl.content_id === contentId
        )

        const qtyOrdered = statusLine?.quantity_ordered ?? (Number(content.quantity) || 0)
        const qtyDelivered = statusLine?.quantity_delivered ?? 0
        const qtyRemaining = statusLine?.quantity_remaining ?? (qtyOrdered - qtyDelivered)
        const isService = statusLine?.is_service ?? (content.reference === null || content.reference === undefined || content.reference === '')

        result.push({
          requirement_id: reqId,
          content_id: contentId,
          detail: statusLine?.detail ?? content.detail ?? '',
          reference: content.reference ?? null,
          is_service: isService,
          quantity_ordered: qtyOrdered,
          quantity_delivered: qtyDelivered,
          quantity_remaining: Math.max(0, qtyRemaining),
          quantity_in_stock: -1,
          sufficient: true,
          quantityToDeliver: 0
        })
      })
    })

    setLines(result)
    setDeliverAll(false)
  }

  useEffect(() => {
    if (stockAvailability.length > 0) {
      setLines(prev => prev.map(line => {
        if (!line.reference) return line
        const matches = stockAvailability.filter((s: any) => s.variant_id === line.reference)
        if (matches.length > 0) {
          const centerMatch = matches.find((s: any) => s.id_storage_center === selectedStorageCenterId)
          const totalStock = centerMatch
            ? centerMatch.quantity_in_stock ?? 0
            : 0
          return {
            ...line,
            quantity_in_stock: totalStock,
            sufficient: totalStock >= line.quantity_remaining,
            is_service: line.is_service
          }
        }
        return line
      }))
    }
  }, [stockAvailability])

  const handleToggleDeliverAll = (checked: boolean) => {
    setDeliverAll(checked)
    setLines(prev => prev.map(line => ({
      ...line,
      quantityToDeliver: checked ? line.quantity_remaining : 0
    })))
  }

  const handleQuantityChange = (index: number, value: string) => {
    const numValue = Math.max(0, Math.min(Number(value) || 0, lines[index].quantity_remaining))
    setLines(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], quantityToDeliver: numValue }
      return updated
    })
    setDeliverAll(false)
  }

  const getStockColor = (line: DeliveryLine): string => {
    if (line.is_service) return 'secondary'
    if (line.quantity_in_stock < 0) return 'secondary'
    if (line.quantity_in_stock === 0) return 'danger'
    if (line.quantity_in_stock < line.quantity_remaining) return 'warning'
    return 'success'
  }

  const selectedAddress = addresses.find((a: any) => a.id === selectedAddressId)

  const submitCreate = async () => {
    if (!companyForUser || !order || !deliveryDate) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      })
      return
    }

    const contentsToDeliver = lines
      .filter(line => line.quantityToDeliver > 0)
      .map(line => ({
        requirement_id: line.requirement_id,
        content_id: line.content_id,
        quantity_delivered: line.quantityToDeliver
      }))

    if (contentsToDeliver.length === 0) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      })
      return
    }

    const allRemaining = lines
      .filter(line => line.quantity_remaining > 0)
      .every(line => line.quantityToDeliver === line.quantity_remaining)

    setLoadIndicator('on')

    await OrderDA.createDeliveryNote(
      companyForUser.id,
      order.id,
      deliveryDate,
      selectedAddressId || undefined,
      selectedStorageCenterId || undefined,
      selectedStorageCenterId ? autoStockOut : undefined,
      allRemaining ? undefined : contentsToDeliver
    ).then((response) => {
      if (response.status === 200 || response.status === 201) {
        const responseData = response.data
        if (responseData?.stock_warnings && responseData.stock_warnings.length > 0) {
          setStockWarnings(responseData.stock_warnings)
        }

        GlobalSwal.fire({
          title: lang.labels.successfullyCreatedDeliveryNote,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        }).then(() => {
          executeHideModal()
          onCreated()
        })
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoadIndicator('off')
    })
  }

  const executeHideModal = () => {
    modalHide('modal-delivery-note-create')

    setTimeout(() => {
      setDeliveryDate(new Date().toISOString().split('T')[0])
      setDeliverAll(false)
      setStockWarnings([])
      setSelectedStorageCenterId('')
      setAutoStockOut(true)
      buildLines()
    }, 200)
  }

  const hasDeliverableLines = lines.some(line => line.quantity_remaining > 0)

  return (
    <div id="modal-delivery-note-create" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.createDeliveryNote}</h2>
          </div>
          <div className="modal-body">
            { order
              ?
              <div className="form">
                <div className="row mb-5">
                  <div className="col-md-6">
                    <div className="fv-row mb-5">
                      <label className="form-label required">{lang.labels.deliveryDate}</label>
                      <input
                        className="form-control"
                        type="date"
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="fv-row mb-5">
                      <label className="form-label">{lang.labels.deliveyAddress}</label>
                      <select
                        className="form-select"
                        value={selectedAddressId}
                        onChange={(e) => setSelectedAddressId(e.target.value)}
                      >
                        <option value="">{lang.labels.selectOption}</option>
                        { addresses.map((addr: any) => (
                          <option key={addr.id} value={addr.id}>
                            {addr.label} — {addr.address?.street ?? ''} {addr.address?.number ?? ''}, {addr.address?.zip_code ?? ''} {addr.address?.city ?? ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                { selectedAddress?.address &&
                  <div className="fv-row mb-5">
                    <div className="bg-light-primary rounded p-3">
                      <span className="fw-bold">{selectedAddress.label}: </span>
                      {selectedAddress.address.street} {selectedAddress.address.number}
                      {selectedAddress.address.additional ? `, ${selectedAddress.address.additional}` : ''}
                      , {selectedAddress.address.zip_code} {selectedAddress.address.city}
                      , {countryName(lang.code, selectedAddress.address.country ?? '')}
                    </div>
                  </div>
                }
                <div className="row mb-5">
                  <div className="col-md-6">
                    <div className="fv-row mb-5">
                      <label className="form-label">{lang.labels.storageCenter ?? 'Storage center'}</label>
                      <select
                        className="form-select"
                        value={selectedStorageCenterId}
                        onChange={(e) => setSelectedStorageCenterId(e.target.value)}
                      >
                        <option value="">{lang.labels.selectOption}</option>
                        { storageCenters.map((center: any) => (
                          <option key={center.id} value={center.id}>
                            {center.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    { selectedStorageCenterId &&
                      <div className="fv-row mb-5 mt-8">
                        <div className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="auto-stock-out-check"
                            checked={autoStockOut}
                            onChange={(e) => setAutoStockOut(e.target.checked)}
                          />
                          <label className="form-check-label fw-bold" htmlFor="auto-stock-out-check">
                            {lang.labels.autoStockOut ?? 'Auto stock out'}
                          </label>
                        </div>
                      </div>
                    }
                  </div>
                </div>
                { stockWarnings.length > 0 &&
                  <div className="alert alert-warning d-flex align-items-center p-3 mb-5">
                    <span className="svg-icon svg-icon-2hx svg-icon-warning me-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path opacity="0.3" d="M2 4V16C2 16.6 2.4 17 3 17H13L16.6 20.6C17.1 21.1 18 20.8 18 20V17H21C21.6 17 22 16.6 22 16V4C22 3.4 21.6 3 21 3H3C2.4 3 2 3.4 2 4Z" fill="black"/>
                        <path d="M18 9H6C5.4 9 5 8.6 5 8C5 7.4 5.4 7 6 7H18C18.6 7 19 7.4 19 8C19 8.6 18.6 9 18 9ZM16 12C16 11.4 15.6 11 15 11H6C5.4 11 5 11.4 5 12C5 12.6 5.4 13 6 13H15C15.6 13 16 12.6 16 12Z" fill="black"/>
                      </svg>
                    </span>
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{lang.labels.stockWarnings ?? 'Stock warnings'}</span>
                      { stockWarnings.map((warning, i) => (
                        <span key={i} className="fs-7">{warning}</span>
                      ))}
                    </div>
                  </div>
                }
                { hasDeliverableLines &&
                  <div className="fv-row mb-5">
                    <div className="form-check form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="deliver-all-check"
                        checked={deliverAll}
                        onChange={(e) => handleToggleDeliverAll(e.target.checked)}
                      />
                      <label className="form-check-label fw-bold" htmlFor="deliver-all-check">
                        {lang.labels.deliverAll}
                      </label>
                    </div>
                  </div>
                }
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed gy-4">
                    <thead>
                      <tr className="fw-bold text-muted text-uppercase fs-7">
                        <th className="min-w-150px">{lang.labels.detail}</th>
                        <th className="text-center w-75px">{lang.labels.type ?? 'Type'}</th>
                        <th className="text-center w-100px">{lang.labels.quantityOrdered}</th>
                        <th className="text-center w-100px">{lang.labels.quantityDelivered}</th>
                        <th className="text-center w-100px">{lang.labels.quantityRemaining}</th>
                        { selectedStorageCenterId &&
                          <th className="text-center w-100px">{lang.labels.quantityInStock ?? 'In stock'}</th>
                        }
                        <th className="text-center w-125px">{lang.labels.quantityToDeliver}</th>
                      </tr>
                    </thead>
                    <tbody className="fw-bold">
                      { lines.map((line, index) => (
                        <tr key={index}>
                          <td>{line.detail}</td>
                          <td className="text-center">
                            <span className={`badge badge-light-${line.is_service ? 'info' : 'primary'} fs-8`}>
                              {line.is_service ? (lang.labels.service ?? 'Service') : (lang.labels.product ?? 'Product')}
                            </span>
                          </td>
                          <td className="text-center">{line.quantity_ordered}</td>
                          <td className="text-center">{line.quantity_delivered}</td>
                          <td className="text-center">{line.quantity_remaining}</td>
                          { selectedStorageCenterId &&
                            <td className="text-center">
                              { line.is_service
                                ? <span className="text-muted">-</span>
                                : line.quantity_in_stock >= 0
                                  ? <span className={`badge badge-light-${getStockColor(line)} fs-8`}>{line.quantity_in_stock}</span>
                                  : <span className="text-muted">-</span>
                              }
                            </td>
                          }
                          <td className="text-center">
                            { line.quantity_remaining > 0
                              ?
                              <input
                                className="form-control form-control-sm text-center"
                                type="number"
                                min={0}
                                max={line.quantity_remaining}
                                value={line.quantityToDeliver}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                              />
                              :
                              <span className="badge badge-light-success fs-8">{lang.labels.alreadyFullyDelivered}</span>
                            }
                          </td>
                        </tr>
                      ))}
                      { lines.length === 0 &&
                        <tr>
                          <td colSpan={selectedStorageCenterId ? 7 : 6} className="text-center text-muted">{lang.labels.noDeliveryNotesYet}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitCreate} disabled={!hasDeliverableLines}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalDeliveryNoteCreate;
